import React, { useState, useRef } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
  getExpandedRowModel,
  ExpandedState,
  ColumnResizeMode,
  Row,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  useDndMonitor,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RxDragHandleDots2 } from 'react-icons/rx';
import TableSkeleton from '../Loaders/TableSkeleton';

interface SortableRowProps {
  id: string;
  children: React.ReactNode;
  className?: string;
  isExpanded?: boolean;
  canDrag?: boolean;
  /** Handed to dnd-kit so a shared drag context can tell what is being dragged. */
  data?: Record<string, unknown>;
  /**
   * With a shared drag context the page draws the drag preview, so the row
   * stays put (dimmed) instead of following the pointer out of its table.
   */
  usesDragOverlay?: boolean;
}

const SortableRow = ({
  id,
  children,
  className,
  canDrag = true,
  data,
  usesDragOverlay = false,
}: SortableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, data });

  const style = {
    transform: usesDragOverlay && isDragging ? undefined : CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : 1,
    opacity: isDragging ? (usesDragOverlay ? 0.4 : 0.8) : 1,
    position: isDragging ? 'relative' : 'static',
  } as React.CSSProperties;

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`group ${className || ''} ${
        isDragging ? 'bg-[#2D3748] shadow-lg border border-primary-subtle' : ''
      } ${!canDrag ? 'cursor-default' : ''}`}
    >
      {React.Children.map(children, (child, index) => {
        if (index === 0 && React.isValidElement(child)) {
          const element = child as React.ReactElement<any>;
          const childrenArray = React.Children.toArray(element.props.children);
          const firstChild = childrenArray[0];

          if (React.isValidElement(firstChild)) {
            const newFirstChild = React.cloneElement(
              firstChild as React.ReactElement<any>,
              {
                className: `${(firstChild.props as any).className || ''} relative group/drag flex-1`,
                children: (
                  <>
                    {canDrag && (
                      <div
                        className="absolute left-[-20px] top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing flex items-center justify-center p-[2px] rounded hover:bg-neutral-medium/20 transition-opacity z-10"
                        {...attributes}
                        {...listeners}
                      >
                        <RxDragHandleDots2
                          size={16}
                          className="text-neutral-medium hover:text-neutral-light"
                        />
                      </div>
                    )}
                    {(firstChild.props as any).children}
                  </>
                ),
              },
            );
            return React.cloneElement(element, {
              children: [newFirstChild, ...childrenArray.slice(1)],
            });
          }
        }
        return child;
      })}
    </tr>
  );
};

interface ResizableTableProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  isLoading?: boolean;
  skeletonRows?: number;
  className?: string;
  getRowCanExpand?: (row: any) => boolean;
  getSubRows?: (originalRow: T) => T[] | undefined;
  getRowId?: (originalRow: T) => string;
  onReorder?: (activeId: string, overId: string) => void;
  renderEmptyState?: () => React.ReactNode;
  isScrollable?: boolean;
  onRowExpand?: (row: T) => void;
  onLoadMoreChildren?: (parentUuid: string) => void;
  getRowCanDrag?: (row: Row<T>) => boolean;
  isFilterApplied?: boolean;
  bottomContent?: React.ReactNode;
  uppercaseHeaders?: boolean;
  /**
   * Joins a drag context the page provides instead of creating one, so rows
   * can be dragged between tables. Drops within this table still reorder via
   * `onReorder`; drops elsewhere are the page's to handle.
   */
  dndContainerId?: string;
}

/** Reorders within one table when it shares the page's drag context. */
const ReorderMonitor = ({
  containerId,
  onReorder,
}: {
  containerId: string;
  onReorder: (activeId: string, overId: string) => void;
}) => {
  useDndMonitor({
    onDragEnd: ({ active, over }) => {
      if (!over || active.id === over.id) return;
      const from = active.data.current?.sortable?.containerId;
      const to = over.data.current?.sortable?.containerId;
      if (from === containerId && to === containerId) {
        onReorder(active.id as string, over.id as string);
      }
    },
  });
  return null;
};

export const ResizableTable = <T extends unknown>({
  data,
  columns,
  isLoading = false,
  skeletonRows = 3,
  className = '',
  getRowCanExpand,
  getSubRows,
  getRowId,
  onReorder,
  renderEmptyState,
  isScrollable = true,
  onRowExpand,
  onLoadMoreChildren,
  getRowCanDrag,
  isFilterApplied = false,
  bottomContent,
  uppercaseHeaders = false,
  dndContainerId,
}: ResizableTableProps<T>) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnResizeMode] = useState<ColumnResizeMode>('onChange');

  // Ref for the virtualized scroll container
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (active.id !== over?.id && onReorder) {
      onReorder(active.id as string, over?.id as string);
    }
  }

  const table = useReactTable({
    data,
    columns,
    state: {
      expanded,
    },
    onExpandedChange: (updater) => {
      const nextExpanded =
        typeof updater === 'function' ? updater(expanded) : updater;
      setExpanded(nextExpanded);

      // If a new row was expanded, trigger the callback
      if (onRowExpand) {
        const expandedIds = Object.keys(nextExpanded);
        const prevExpandedIds = Object.keys(expanded);
        const newlyExpandedId = expandedIds.find(
          (id) => !prevExpandedIds.includes(id),
        );

        if (
          newlyExpandedId &&
          (nextExpanded as Record<string, boolean>)[newlyExpandedId]
        ) {
          const row = table.getRow(newlyExpandedId);
          if (row) {
            onRowExpand(row.original);
          }
        }
      }
    },
    getSubRows,
    getRowCanExpand,
    getRowId,
    columnResizeMode,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    defaultColumn: {
      minSize: 60,
      size: 150,
      maxSize: 800,
    },
  });

  // Flat list of all visible rows (respects expansion state)
  const rows = table.getRowModel().rows;

  // ─── Row Virtualizer ──────────────────────────────────────────────────────────
  // Only renders the ~25-30 rows currently in the viewport.
  // All features (tree expansion, DnD, column resize) continue to work correctly.
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,

    estimateSize: (index) => {
      const row = rows[index];
      if (!row) return 48;
      const original = row.original as any;
      if (original?.isLoadMore || original?.isLoading) return 36;
      return 48;
    },
    overscan: 8, // render 8 extra rows above/below for smooth scrolling
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalVirtualHeight = rowVirtualizer.getTotalSize();

  // Padding to fill the virtual space above/below rendered rows
  const paddingTop = virtualRows.length > 0 ? (virtualRows[0]?.start ?? 0) : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalVirtualHeight - (virtualRows[virtualRows.length - 1]?.end ?? 0)
      : 0;

  // DnD: SortableContext still receives ALL draggable IDs so drop targets
  // work correctly across the full list, even for non-rendered rows.
  const items = React.useMemo(() => {
    if (!getRowId || !onReorder) return [];
    return rows
      .filter((row) => !getRowCanDrag || getRowCanDrag(row))
      .map((row) => getRowId(row.original));
  }, [rows, getRowId, onReorder, getRowCanDrag]);

  // ─── Row renderer (used for both normal and virtual rows) ────────────────────
  const renderRow = (row: (typeof rows)[number]) => {
    const isDraggable = !!onReorder && !!getRowId;
    const rowClass =
      'border-b border-[#2A2F3D] bg-transparent hover:bg-[#1E2230] transition-all duration-300 text-neutral';
    const isLoadingRow = (row.original as any)?.isLoading;
    const isLoadMoreRow = (row.original as any)?.isLoadMore;

    if (isLoadingRow) {
      return (
        <tr
          key={row.id}
          className="border-b border-primary-intemediate bg-primary/40 animate-pulse"
        >
          {row.getVisibleCells().map((cell, index) => {
            const isFirstColumn = index === 0;
            return (
              <td
                key={cell.id}
                style={{
                  width: cell.column.getSize(),
                  minWidth: cell.column.getSize(),
                  paddingLeft: isFirstColumn
                    ? `${(isFilterApplied ? 0 : row.depth) * 24 + 16}px`
                    : '16px',
                }}
                className="py-4 border-r border-[#2A2F3D] overflow-hidden"
              >
                <div className="h-4 bg-white/10 rounded w-3/4"></div>
              </td>
            );
          })}
        </tr>
      );
    }

    if (isLoadMoreRow) {
      return (
        <tr
          key={row.id}
          className="border-b border-primary-intemediate bg-primary hover:bg-blue-500/5 cursor-pointer transition-colors group/row"
          onClick={(e) => {
            e.stopPropagation();
            if (onLoadMoreChildren) {
              onLoadMoreChildren((row.original as any).parentWorkItemUuid);
            }
          }}
        >
          <td
            colSpan={table.getVisibleFlatColumns().length}
            style={{
              paddingLeft: `${(isFilterApplied ? 0 : row.depth) * 24 + 16}px`,
            }}
            className="py-3 pr-4 border-r border-[#2A2F3D] overflow-hidden"
          >
            <div className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-all duration-200 text-xs font-bold uppercase tracking-widest group/btn">
              <div className="p-1 rounded bg-blue-500/10 group-hover/btn:bg-blue-500/20 transition-colors">
                <svg
                  className="w-3.5 h-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="7 13 12 18 17 13"></polyline>
                  <polyline points="7 6 12 11 17 6"></polyline>
                </svg>
              </div>
              <span>Show more items...</span>
            </div>
          </td>
        </tr>
      );
    }

    const cells = row.getVisibleCells().map((cell, index) => {
      const isFirstColumn = index === 0;
      return (
        <td
          key={cell.id}
          style={{
            width: cell.column.getSize(),
            minWidth: cell.column.getSize(),
            paddingLeft: isFirstColumn
              ? `${(isFilterApplied ? 0 : row.depth) * 24 + 16}px`
              : '16px',
          }}
          className="relative py-3 pr-4 whitespace-nowrap border-r border-[#2A2F3D] overflow-hidden group/cell"
        >
          {/* Indentation Guide Line */}
          {isFirstColumn && !isFilterApplied && row.depth > 0 && (
            <div
              className="absolute left-0 top-0 bottom-0 border-l border-white/10"
              style={{ left: `${(row.depth - 1) * 24 + 26}px` }}
            />
          )}
          <div className="flex items-center gap-2 w-full min-w-0 pr-1">
            {/* Render expander or an empty spacer to keep columns perfectly aligned */}
            {isFirstColumn && (!!getSubRows || !!getRowCanExpand) && (
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                {row.getCanExpand() && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      row.toggleExpanded();
                    }}
                    className="w-5 h-5 flex items-center justify-center shrink-0 rounded hover:bg-white/10"
                  >
                    {row.getIsExpanded() ? (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    ) : (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    )}
                  </button>
                )}
              </div>
            )}

            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </div>

          {/* Column Resizer Element (Available on every cell) */}
          {table
            .getFlatHeaders()
            .find((h: any) => h.column.id === cell.column.id) && (
            <div
              {...{
                onDoubleClick: () => cell.column.resetSize(),
                onMouseDown: table
                  .getFlatHeaders()
                  .find((h: any) => h.column.id === cell.column.id)
                  ?.getResizeHandler(),
                onTouchStart: table
                  .getFlatHeaders()
                  .find((h: any) => h.column.id === cell.column.id)
                  ?.getResizeHandler(),
                className: `absolute right-0 top-0 h-full w-1.5 cursor-col-resize select-none touch-none hover:bg-purple-500 z-20 ${
                  cell.column.getIsResizing()
                    ? 'bg-purple-500 opacity-100'
                    : 'opacity-0 group-hover/cell:bg-purple-500/50 group-hover/cell:opacity-100'
                }`,
              }}
            />
          )}
        </td>
      );
    });

    if (isDraggable) {
      const canDrag = getRowCanDrag ? getRowCanDrag(row) : true;
      return (
        <SortableRow
          key={row.id}
          id={getRowId!(row.original)}
          className={rowClass}
          canDrag={canDrag}
          data={{ row: row.original }}
          usesDragOverlay={!!dndContainerId}
        >
          {cells}
        </SortableRow>
      );
    }

    return (
      <tr key={row.id} className={rowClass}>
        {cells}
      </tr>
    );
  };

  const renderTable = () => (
    <div
      className={`w-full border border-[#2A2F3D] rounded-lg flex flex-col overflow-hidden ${isScrollable ? 'h-full flex-1 min-h-0' : ''} ${
        // Nothing to scroll to when empty, so don't show a horizontal scrollbar for the header alone.
        !isScrollable && (isLoading || data.length > 0) ? 'overflow-x-auto' : ''
      }`}
    >
      <div
        ref={scrollContainerRef}
        className={`w-full flex flex-col ${isScrollable ? 'h-full overflow-auto custom-scrollbar' : ''}`}
      >
        <table
          className="w-full font-semibold text-[#344054] text-sm border-collapse table-fixed"
          style={{ width: '100%', minWidth: table.getTotalSize() }}
        >
          {/* Table Header */}
          <thead className="bg-primary text-[#9CA3AF] md:sticky top-0 z-30 shadow-sm">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{
                        width: header.getSize(),
                        minWidth: header.getSize(),
                      }}
                      className={`relative px-4 py-3 text-left text-xs font-semibold tracking-wider border-b border-r border-[#2A2F3D] whitespace-nowrap overflow-hidden group ${
                        uppercaseHeaders ? 'uppercase' : ''
                      }`}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}

                      {/* Column Resizer Element */}
                      <div
                        {...{
                          onDoubleClick: () => header.column.resetSize(),
                          onMouseDown: header.getResizeHandler(),
                          onTouchStart: header.getResizeHandler(),
                          className: `absolute right-0 top-0 h-full w-1.5 cursor-col-resize select-none touch-none group-hover:bg-purple-500/50 hover:bg-purple-500/50 z-20 ${
                            header.column.getIsResizing()
                              ? 'bg-purple-500'
                              : 'bg-transparent'
                          }`,
                        }}
                      />
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody className="bg-transparent">
            {isLoading ? (
              <TableSkeleton
                headers={columns.map((c: any) => c.header || '')}
              />
            ) : data.length > 0 ? (
              <>
                {/* Top padding spacer — fills virtual space above rendered rows */}
                {paddingTop > 0 && (
                  <tr>
                    <td
                      colSpan={columns.length}
                      style={{
                        height: `${paddingTop}px`,
                        padding: 0,
                        border: 0,
                      }}
                    />
                  </tr>
                )}

                {/* Only the visible rows are rendered */}
                {virtualRows.map((virtualRow) =>
                  renderRow(rows[virtualRow.index]),
                )}

                {/* Bottom padding spacer — fills virtual space below rendered rows */}
                {paddingBottom > 0 && (
                  <tr>
                    <td
                      colSpan={columns.length}
                      style={{
                        height: `${paddingBottom}px`,
                        padding: 0,
                        border: 0,
                      }}
                    />
                  </tr>
                )}
              </>
            ) : null}
          </tbody>
        </table>

        {!isLoading && data.length === 0 && (
          <div className="flex-1 flex items-center justify-center p-8 bg-transparent">
            {renderEmptyState ? (
              renderEmptyState()
            ) : (
              <div className="text-center text-neutral-medium">
                No data available
              </div>
            )}
          </div>
        )}

        {bottomContent}
      </div>
    </div>
  );

  return (
    <div className={`relative w-full flex flex-col min-h-0 ${className}`}>
      {onReorder && getRowId && dndContainerId ? (
        <SortableContext id={dndContainerId} items={items} strategy={verticalListSortingStrategy}>
          <ReorderMonitor containerId={dndContainerId} onReorder={onReorder} />
          {renderTable()}
        </SortableContext>
      ) : onReorder && getRowId ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            {renderTable()}
          </SortableContext>
        </DndContext>
      ) : (
        renderTable()
      )}
    </div>
  );
};
